import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { siteInfo } from "@/mocks/meta";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "搜索", to: "/search" },
  { label: "关于", to: "/about" },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
  };

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to.split("?")[0]);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled
          ? "border-background-200 bg-background-50/95 backdrop-blur"
          : "border-transparent bg-background-50"
      }`}
    >
      <div className="mx-auto flex h-16 w-full items-center gap-4 px-4 md:h-20 md:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-700 text-background-50">
            <i className="ri-clapperboard-line text-xl" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-extrabold tracking-tight text-foreground-950 md:text-xl">
              {siteInfo.name}
            </span>
            <span className="mt-1 hidden text-[11px] font-medium tracking-wide text-foreground-500 sm:block">
              影视资料 · 剧情演员
            </span>
          </span>
        </Link>

        <nav className="ml-4 hidden items-center gap-1 md:flex lg:ml-8">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors lg:px-4 ${
                isActive(item.to)
                  ? "bg-primary-50 text-primary-700"
                  : "text-foreground-600 hover:bg-background-100 hover:text-foreground-950"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <form
          onSubmit={handleSearch}
          className="ml-auto hidden w-56 items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2 transition-colors focus-within:border-primary-400 lg:flex xl:w-72"
        >
          <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-base" />
          </span>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索影视 / 演员 / 导演"
            className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
          />
        </form>

        <Link
          to="/search"
          aria-label="搜索"
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:bg-background-100 lg:hidden"
        >
          <i className="ri-search-line text-lg" />
        </Link>

        <button
          type="button"
          aria-label="菜单"
          onClick={() => setMenuOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:bg-background-100 md:hidden"
        >
          <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-3 md:hidden">
          <form
            onSubmit={handleSearch}
            className="mb-3 flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2"
          >
            <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base" />
            </span>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视 / 演员 / 导演"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
          </form>
          <div className="grid grid-cols-2 gap-2">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center justify-between rounded-md px-3 py-3 text-sm font-medium transition-colors ${
                  isActive(item.to)
                    ? "bg-primary-50 text-primary-700"
                    : "bg-background-100 text-foreground-700"
                }`}
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-base" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}